/**
 * Autosuggested inputs for a blacklist.
 */
import * as React from 'react';
import * as AutoSuggest from 'react-autosuggest';
import { BlacklistNode } from './Blacklist';
import { observer, inject } from 'mobx-react';
import { PostStore } from '../../../stores/PostStore';
import { intercept } from 'mobx';

interface BlacklistInputProps {
    onChange: (e: string) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    node: BlacklistNode[];
    innerNode: BlacklistNode;
    innerNodeRef: number;
    tags: string[];
    savedInput?: string;
}

type BlacklistInputFullProps = BlacklistInputProps & {
    postStore?: PostStore;
};

/**
 * An autocompletable blacklist input for the blacklist tag system, pulls from all tags.
 * 
 * @export
 * @class BlacklistInput
 * @extends {React.Component<BlacklistInputFullProps>}
 */
@inject(stores => ({
    postStore: stores.postStore as PostStore,
}))
@observer
export class BlacklistInput extends React.Component<BlacklistInputFullProps> {
    state = {
        currentSuggestions: [],
        value: this.props.savedInput || '',
    };

    constructor(props: BlacklistInputFullProps) {
        super(props);
        intercept(this.props.innerNode, 'value', change => {
            this.setState({
                // tslint:disable-next-line:no-any
                value: change.newValue,
            });
            return change;
        });
    }

    getSuggestions = (input: string) => {
        const tags = this.props.postStore!.flatTags;
        return (
            tags &&
            tags
                .filter(t => t.toLowerCase().startsWith(input.toLowerCase()))
                .map(value => ({ value }))
        );
    }

    onSuggestionsFetchRequested = ({ value }: { value: string }) => {
        this.setState({
            currentSuggestions: this.getSuggestions(value),
        });
    }

    onSuggestionsClearRequested = () => {
        this.setState({
            currentSuggestions: [],
        });
    }

    onChange = (e: React.ChangeEvent<HTMLInputElement>, v: { newValue: string } | 'string') => {
        const value = typeof v === 'string' ? v : v.newValue;

        this.setState({
            value,
        });

        this.props.onChange(value || e.target.value);
    }

    shouldRenderSuggestions = (value: string) =>
        value.trim().length > 2 && window.matchMedia('(min-width: 1080px)').matches

    renderSuggestion = (suggestion: { value: string }) => <div>{suggestion.value}</div>;

    render() {
        return (
            <AutoSuggest
                suggestions={this.state.currentSuggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                renderSuggestion={this.renderSuggestion}
                shouldRenderSuggestions={this.shouldRenderSuggestions}
                getSuggestions={this.getSuggestions}
                getSuggestionValue={({ value }: { value: string }) => value}
                inputProps={{
                    value: this.state.value,
                    placeholder: 'tag',
                    className: 'Blacklist-input Theme-background',
                    size: 7,
                    onChange: this.onChange,
                    onKeyDown: this.props.onKeyDown,
                }}
                theme={{
                    suggestionsContainer: 'Blacklist-input-suggester Theme-background',
                    suggestion: 'Blacklist-suggestion',
                    suggestionHighlighted: 'Blacklist-suggestion-active',
                }}
                id={this.props.innerNode.id}
                ref={(nodeRef: { input: HTMLInputElement }) => {
                    if (nodeRef && this.props.innerNode.shouldFocus) {
                        nodeRef.input.focus();
                        this.props.innerNode.shouldFocus = false; // should only run once.
                    }
                }}
            />
        );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/Inputs/Blacklist/BlacklistInput.tsx